import { Box, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useAppSelector } from '../../../../../store/hooks';
import zeplinColor from '../../../../../theme/Color';
import { CalendarEvent } from '../../../../../utilities/eventUtilies';
import CustomOverlapImg from '../../../../shared/CustomOverlapImg/CustomOverlapImg';

const CalendarCard = ({ event, height }: { event: CalendarEvent; height: number }) => {
  const { start, end, meetingName, meetingLink: link, invitees, type, selectedCalendar } = event;
  const { colors, accounts } = useAppSelector((state) => state.calendar);

  // derive provider and email from selectedCalendar (e.g., "google:john@doe.com")
  const [provider, email] = (selectedCalendar || '').split(':');
  let accountColor: string | undefined;
  let accountListKey: string;
  if (provider && email) {
    accountListKey =
      provider === 'google'
        ? 'googleAccounts'
        : provider === 'zoom'
        ? 'zoomAccounts'
        : provider === 'microsoft'
        ? 'msTeamsAccounts'
        : '';

    const providerAccounts =
      accountListKey && accounts ? accounts[accountListKey as keyof typeof accounts] : undefined;
    const matchedAccount = Array.isArray(providerAccounts)
      ? providerAccounts.find((acc: any) => acc?.email === email)
      : undefined;
    accountColor = matchedAccount?.color || colors[`${provider}:${email}`] || zeplinColor.Brand1;
  }

  const eventColor =
    accountColor ||
    colors[selectedCalendar] ||
    colors[type] ||
    colors['huddle'] ||
    zeplinColor.Brand1;

  return (
    <Box
      sx={{
        backgroundColor: eventColor,
        border: `1px solid ${eventColor}`,
        borderRadius: '16px',
        padding: '10px 12px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        height: height,
        boxSizing: 'border-box',
        overflow: 'hidden',
        '&:hover': {
          overflowY: 'auto',
        },
        '&::-webkit-scrollbar': { display: 'none' },
        msOverflowStyle: 'none',
        scrollbarWidth: 'none',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <Typography
            variant='responsiveBody3'
            sx={{ fontWeight: 500, color: 'rgba(0,0,0,0.85)', lineHeight: 1.2 }}
          >
            {meetingName}
          </Typography>
          <Typography
            variant='responsiveLabel1'
            sx={{ color: 'rgba(0,0,0,0.5)', fontSize: '0.7rem' }}
          >
            {`${dayjs(start).format('hh:00a')} - ${dayjs(end).format('hh:00a')}`}
          </Typography>
        </Box>

        {link && (
          <Box
            sx={{
              backgroundColor: zeplinColor.Neutral1,
              borderRadius: '8px',
              padding: '6px 8px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              width: 'fit-content',
              maxWidth: '100%',
              boxShadow: '0px 1px 3px rgba(0,0,0,0.05)',
              border: '1px solid rgba(0,0,0,0.03)',
            }}
          >
            <img
              src={'/assets/calendar-video.svg'}
              style={{ width: '14px', height: '14px', objectFit: 'contain' }}
              alt='video-icon'
            />
            <a
              href={link}
              target='_blank'
              rel='noopener noreferrer'
              style={{ textDecoration: 'none' }}
            >
              <Typography
                sx={{
                  maxWidth: '120px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'block',
                  color: 'rgba(0,0,0,0.6)',
                  fontSize: '0.75rem',
                }}
                variant='responsiveLabel1'
              >
                {link.replace(/^https?:\/\//, '')}
              </Typography>
            </a>
          </Box>
        )}
        {invitees && invitees.length > 0 && (
          <Box sx={{ pt: 1 }}>
            <CustomOverlapImg
              images={invitees.map((val: any) => val?.profileImage) || []}
              name={invitees.map((val: any) => val?.name) || []}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default CalendarCard;
